import { makeStyles } from '@material-ui/styles';
import { format, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import IconInBox from 'src/common/ui/components/IconInBox/IconInBox';
import * as BrowserNotification from 'src/components/general/BrowserNotification';
import {
  PROVIDER_IN_VIDEO_CALL,
  isProviderInVideoCall,
} from 'src/components/pages/pageElements/videoConference/providerInVideoCall';
import { FyiBanner } from 'src/myDayToday/components/FyiBanner';
import { UnsignedVisitNotesSection } from 'src/myDayToday/components/UnsignedVisitNotesSection';
import {
  AcceptNextPatientAcceptMode,
  AcceptNextPatientProviderStatus,
  AcceptNextPatientScheduleItemType,
} from 'src/myDayToday/types/acceptNextPatientCard';
import type { AcceptNextPatientCardProps } from 'src/myDayToday/types/acceptNextPatientCard';
import type { CardName, CardState } from 'src/observability/staffEventTypes';
import { useObservability } from 'src/observability/useObservability';
import { ReactComponent as IconClockBlue } from 'src/shared/icons/iconClockBlue.svg';
import { ReactComponent as IconMedicalBlue } from 'src/shared/icons/iconMedicalBlue.svg';
import { ReactComponent as IconSupportBlue } from 'src/shared/icons/iconSupportBlue.svg';

export {
  AcceptNextPatientAcceptMode,
  AcceptNextPatientProviderStatus,
  AcceptNextPatientScheduleItemType,
} from 'src/myDayToday/types/acceptNextPatientCard';
export type { AcceptNextPatientCardProps } from 'src/myDayToday/types/acceptNextPatientCard';

/**
 * The number of minutes at the end of a provider's availability where there is not enough time to
 * handle intakes or reengagements; they should be limited to followups during this time.
 */
export const WINDING_DOWN_THRESHOLD_MINUTES = 40;

// While a notification is deferred, re-check this often whether the provider has left the call.
// Covers the crash path (tab killed without `beforeunload`) where no `storage` event is emitted
// and the call flag only clears via providerInVideoCall's 60s stale TTL.
const DEFERRED_RECHECK_MS = 30_000;

function showNewPatientNotification(): void {
  BrowserNotification.show('Boulder Care Staff App', {
    renotify: true,
    tag: 'BoulderCare:Staff',
    requireInteraction: true,
    body: 'A patient is ready to be accepted.',
    icon: '/logoBrowserNotification.png',
    image: '/logoBrowserNotification.png',
  } as ConstructorParameters<typeof Notification>[1]);
}

export function AcceptNextPatientCard({
  section,
  unsignedVisits,
  onAccept,
  onVisitClick,
  isAccepting,
  currentTime,
}: AcceptNextPatientCardProps): JSX.Element {
  const styles = useStyles();
  const sendStaffEvent = useObservability(true);
  const now = currentTime ?? new Date();
  const prevAcceptMode = useRef(section?.acceptMode ?? null);
  // A null→non-null transition we suppressed because the provider was on a call. We owe this
  // notification once the call ends, as long as a patient is still waiting.
  const [notificationDeferred, setNotificationDeferred] = useState(false);

  useEffect(() => {
    const prev = prevAcceptMode.current;
    const current = section?.acceptMode ?? null;
    if (prev === null && current !== null) {
      if (isProviderInVideoCall()) {
        // Don't interrupt a provider already on a video call in another tab — defer instead.
        setNotificationDeferred(true);
      } else {
        showNewPatientNotification();
      }
    }
    // No patient waiting anymore: nothing left to defer.
    if (current === null) {
      setNotificationDeferred(false);
    }
    prevAcceptMode.current = current;
  }, [section?.acceptMode]);

  // Fire a deferred notification as soon as the provider leaves the call — whether the call tab
  // closes cleanly (a `storage` event from the other tab) or crashes (no event; the flag only
  // clears via the 60s stale TTL, so we also poll). Only active while a notification is deferred.
  useEffect(() => {
    if (!notificationDeferred) return undefined;

    const fireIfFree = (): void => {
      if (prevAcceptMode.current !== null && !isProviderInVideoCall()) {
        showNewPatientNotification();
        setNotificationDeferred(false);
      }
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key === PROVIDER_IN_VIDEO_CALL) fireIfFree();
    };
    window.addEventListener('storage', onStorage);
    const poll = setInterval(fireIfFree, DEFERRED_RECHECK_MS);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(poll);
    };
  }, [notificationDeferred]);

  const scheduleItem = section?.scheduleItem ?? null;
  const acceptMode = section?.acceptMode ?? null;
  const status = section?.status ?? AcceptNextPatientProviderStatus.Online;

  const isOffline = status === AcceptNextPatientProviderStatus.Offline;
  const hasPatients = acceptMode !== null;
  const showNothingUpcomingIcon =
    acceptMode === AcceptNextPatientAcceptMode.Any && scheduleItem === null;

  const cardName: CardName = (() => {
    if (isOffline) {
      return 'offline';
    } else if (!hasPatients) {
      return 'no_patients_available';
    } else if (acceptMode === AcceptNextPatientAcceptMode.FollowUpOnly) {
      return 'accept_follow_up_only';
    } else {
      return 'accept_next_patient';
    }
  })();

  const cardState: CardState | undefined = (() => {
    if (isOffline) {
      return undefined;
    }

    // Intentionally include schedule item even for no_patients_available cards.
    // Queue status and schedule context can both be true at the same time.

    switch (scheduleItem?.type) {
      case AcceptNextPatientScheduleItemType.ScheduledBreak:
        return 'impending_block';
      case AcceptNextPatientScheduleItemType.ScheduledVisit:
        return 'impending_event';
      case AcceptNextPatientScheduleItemType.EndOfDay:
        return 'impending_end_of_day';
      default:
        return undefined;
    }
  })();

  useEffect(() => {
    sendStaffEvent({
      event_type: 'card_viewed',
      card_type: 'visits_card',
      card_name: cardName,
      ...(cardState ? { card_state: cardState } : {}),
    });
  }, [cardName, cardState, sendStaffEvent]);

  const buttonLabel =
    acceptMode === AcceptNextPatientAcceptMode.FollowUpOnly
      ? 'ACCEPT FOLLOW-UP VISIT'
      : 'ACCEPT NEXT PATIENT';

  const bodyText = (() => {
    if (!hasPatients) return null;
    if (
      acceptMode === AcceptNextPatientAcceptMode.FollowUpOnly &&
      scheduleItem?.type === AcceptNextPatientScheduleItemType.ScheduledBreak
    ) {
      return `Only follow-ups available <${WINDING_DOWN_THRESHOLD_MINUTES} min before a break.`;
    }
    if (
      acceptMode === AcceptNextPatientAcceptMode.FollowUpOnly &&
      scheduleItem?.type === AcceptNextPatientScheduleItemType.EndOfDay
    ) {
      return `Only follow-ups available <${WINDING_DOWN_THRESHOLD_MINUTES} min before the end of your availability.`;
    }
    if (
      acceptMode === AcceptNextPatientAcceptMode.FollowUpOnly &&
      scheduleItem?.type === AcceptNextPatientScheduleItemType.ScheduledVisit
    ) {
      return `Only follow-ups available <${WINDING_DOWN_THRESHOLD_MINUTES} min before a visit.`;
    }
    return "You'll be assigned the next patient in line.";
  })();

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <IconInBox>
            <IconInBox.Videocam />
          </IconInBox>
          <span className={styles.headerTitle}>Visits</span>
        </div>
      </div>

      {/* Body */}
      <div className={styles.body}>
        {/* FYI Banner (for any schedule item) */}
        {scheduleItem && <FyiBanner scheduleItem={scheduleItem} now={now} />}

        {/* Offline state */}
        {isOffline && (
          <>
            <IconClockBlue width={64} height={64} />
            <span className={styles.primaryText}>You&apos;re offline</span>
            <div className={styles.subTextGroup}>
              {section?.nextAvailability && (
                <p className={styles.subText}>
                  Next availability:{' '}
                  {format(parseISO(section.nextAvailability), "EEEE 'at' h:mm a")}
                </p>
              )}
              <p className={styles.subText}>Thank you for the care you bring to your patients.</p>
            </div>
          </>
        )}

        {/* No patients in line */}
        {!isOffline && !hasPatients && (
          <>
            <IconSupportBlue width={64} height={64} />
            <span className={styles.primaryText}>No patients you can see in line!</span>
            <p className={styles.subText}>
              You&apos;ll be notified when a patient you can see joins.
            </p>
          </>
        )}

        {/* Patients in line – nothing upcoming (show medical icon) */}
        {showNothingUpcomingIcon && <IconMedicalBlue width={64} height={64} />}

        {/* Patients in line – accept button */}
        {hasPatients && !isOffline && (
          <>
            <button
              type="button"
              className={styles.acceptButton}
              onClick={onAccept}
              disabled={isAccepting}
            >
              {buttonLabel}
            </button>
            <div className={styles.subTextGroup}>
              {bodyText && <p className={styles.subText}>{bodyText}</p>}
              <p className={styles.subText}>
                You&apos;ll have time to review and prep before the visit starts.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Unsigned Visit Notes */}
      <UnsignedVisitNotesSection
        unsignedVisits={unsignedVisits}
        now={now}
        onVisitClick={onVisitClick}
      />
    </div>
  );
}

const useStyles = makeStyles({
  card: {
    background: Colors.White,
    borderRadius: 12,
    boxShadow: '0 2px 2px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 24,
    gap: 16,
    width: '100%',
    minWidth: 405,
    boxSizing: 'border-box',
    fontFamily: '"Nunito", "Nunito Sans"',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  headerTitle: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 16,
    fontWeight: 600,
    color: Colors.CoolGray8,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  primaryText: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 16,
    fontWeight: 600,
    color: Colors.CoolGray9,
    textAlign: 'center',
  },
  subTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
    textAlign: 'center',
  },
  subText: {
    margin: 0,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 14,
    fontWeight: 400,
    color: Colors.CoolGray9,
    textAlign: 'center',
    width: '100%',
  },
  acceptButton: {
    background: Colors.BlueSpruce,
    border: 'none',
    borderRadius: 2,
    color: Colors.White,
    cursor: 'pointer',
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    padding: '8px 11px',
    textTransform: 'uppercase',
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    '&:hover:not(:disabled)': {
      opacity: 0.9,
    },
  },
});
